import React, { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import Panel from '../../components/Panel'
import Badge from '../../components/Badge'
import Paginator from '../../components/Table/Paginator'
import { useAppSelector } from '../../shared/redux/hooks'
import QueryError from './QueryError'
import ConductorFieldKey from './ConductorFieldKey'
import { TD, TH } from './conductorTableCells'
import {
  formatConductorTimestamp,
  mutationErrorMessage,
  paginatedListMode,
  shouldShowPaginator,
} from './conductorUtils'
import { useClampedPage } from './useClampedPage'
import {
  useCancelConductorActionMutation,
  useGetConductorOutboxQuery,
  useRetryConductorActionMutation,
} from './conductorApi'

import type { ConductorApprovalResult, ConductorOutboxAction } from './conductor.types'

const PAGE_SIZE = 50
const FILTER_TEXT_MAX = 200

const OUTBOX_STATUSES = [
  'PENDING',
  'CLAIMED',
  'EXECUTING',
  'AWAITING_RECONCILE',
  'SUCCEEDED',
  'FAILED',
  'CANCELLED',
] as const

const AUTONOMY_TIERS = ['AUTO', 'APPROVE', 'HUMAN_ONLY'] as const

const selectClasses =
  'rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100'

const inputClasses =
  'rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100'

function paramOrEmpty(params: URLSearchParams, key: string): string {
  return params.get(key) ?? ''
}

function allowedOrEmpty(value: string, allowed: readonly string[]): string {
  return allowed.includes(value) ? value : ''
}

const OutboxRow: React.FC<{
  action: ConductorOutboxAction
  canApprove: boolean
  busy: boolean
  outcome?: ConductorApprovalResult['outcome']
  mutationError?: string
  onRetry: () => void
  onCancel: (reason: string) => void
}> = ({ action, canApprove, busy, outcome, mutationError, onRetry, onCancel }) => {
  const [cancelling, setCancelling] = useState(false)
  const [reason, setReason] = useState('')
  const failed = action.status === 'FAILED'
  const canRetry = canApprove && failed && action.autonomyTier !== 'HUMAN_ONLY'
  const canCancel = canApprove && failed

  return (
    <>
      <tr className="border-t border-slate-100 dark:border-slate-700">
        <TD>{formatConductorTimestamp(action.createdAt)}</TD>
        <TD className="font-mono text-xs">{action.actionType}</TD>
        <TD className="font-mono text-xs">{action.tool}</TD>
        <TD>
          <Badge status={action.status} />
        </TD>
        <TD>
          {action.lastError == null || action.lastError === '' ? (
            <span className="text-slate-400 dark:text-slate-500">—</span>
          ) : (
            <span className="block max-w-md truncate" title={action.lastError}>
              {action.lastError}
            </span>
          )}
        </TD>
        <TD>{action.autonomyTier}</TD>
        <TD>
          <Link
            to={`/conductor/instances/${action.workflowInstanceCuid}`}
            className="text-indigo-600 hover:underline dark:text-indigo-400"
          >
            {action.subjectCuid}
          </Link>
          <span className="ml-1 text-xs text-slate-400 dark:text-slate-500">{action.subjectType}</span>
        </TD>
        <TD>
          {outcome != null ? (
            <span className="text-xs font-medium text-slate-600 dark:text-slate-300">
              {outcome === 'NOT_ELIGIBLE' ? 'Already settled by someone else' : outcome}
            </span>
          ) : canRetry || canCancel ? (
            <div className="flex flex-wrap items-center gap-2">
              {canRetry && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={onRetry}
                  className="rounded-md bg-green-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-green-500 disabled:opacity-50"
                >
                  Retry action
                </button>
              )}
              {canCancel && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setCancelling((v) => !v)}
                  className="rounded-md border border-red-300 px-2.5 py-1 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-950"
                >
                  Cancel action
                </button>
              )}
            </div>
          ) : (
            <span className="text-slate-400 dark:text-slate-500">—</span>
          )}
        </TD>
      </tr>
      {mutationError != null && (
        <tr>
          <td colSpan={8} className="px-3 py-2 text-xs text-red-600 dark:text-red-400">
            {mutationError}
          </td>
        </tr>
      )}
      {cancelling && outcome == null && (
        <tr>
          <td colSpan={8} className="px-3 py-2">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Reason for cancel (required)"
                aria-label="Cancel reason"
                className={`${inputClasses} flex-1`}
              />
              <button
                type="button"
                disabled={busy || reason.trim() === ''}
                onClick={() => onCancel(reason.trim())}
                className="rounded-md bg-red-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-red-500 disabled:opacity-50"
              >
                Confirm cancel
              </button>
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

export const Outbox: React.FC = () => {
  const { permissions } = useAppSelector((state) => state.app)
  const canApprove = permissions?.includes('conductor:approve') ?? false
  const [busyCuids, setBusyCuids] = useState<ReadonlySet<string>>(() => new Set())
  const [outcomes, setOutcomes] = useState<Record<string, ConductorApprovalResult['outcome']>>({})
  const [mutationErrors, setMutationErrors] = useState<Record<string, string>>({})
  const [retryAction] = useRetryConductorActionMutation()
  const [cancelAction] = useCancelConductorActionMutation()

  const markBusy = (cuid: string): void => {
    setBusyCuids((prev) => new Set(prev).add(cuid))
  }

  const markIdle = (cuid: string): void => {
    setBusyCuids((prev) => {
      const next = new Set(prev)
      next.delete(cuid)
      return next
    })
  }

  const clearMutationError = (cuid: string): void => {
    setMutationErrors((prev) => {
      const next = { ...prev }
      delete next[cuid]
      return next
    })
  }

  const handleRetry = async (cuid: string): Promise<void> => {
    markBusy(cuid)
    clearMutationError(cuid)
    try {
      const result = await retryAction(cuid).unwrap()
      setOutcomes((prev) => ({ ...prev, [cuid]: result.outcome }))
    } catch (error) {
      setMutationErrors((prev) => ({ ...prev, [cuid]: mutationErrorMessage(error) }))
    } finally {
      markIdle(cuid)
    }
  }

  const handleCancel = async (cuid: string, reason: string): Promise<void> => {
    markBusy(cuid)
    clearMutationError(cuid)
    try {
      const result = await cancelAction({ cuid, reason }).unwrap()
      setOutcomes((prev) => ({ ...prev, [cuid]: result.outcome }))
    } catch (error) {
      setMutationErrors((prev) => ({ ...prev, [cuid]: mutationErrorMessage(error) }))
    } finally {
      markIdle(cuid)
    }
  }

  const [searchParams, setSearchParams] = useSearchParams()
  const [page, setPage] = useState(0)
  const status = allowedOrEmpty(paramOrEmpty(searchParams, 'status'), OUTBOX_STATUSES)
  const actionType = paramOrEmpty(searchParams, 'actionType')
  const lastErrorContains = paramOrEmpty(searchParams, 'lastErrorContains')
  const autonomyTier = allowedOrEmpty(paramOrEmpty(searchParams, 'autonomyTier'), AUTONOMY_TIERS)
  const rawSubjectCuid = paramOrEmpty(searchParams, 'subjectCuid')
  const subjectCuid = rawSubjectCuid.trim()
  const [actionTypeDraft, setActionTypeDraft] = useState(actionType)
  const [lastErrorDraft, setLastErrorDraft] = useState(lastErrorContains)
  const filterKey = [status, actionType, lastErrorContains, autonomyTier, subjectCuid].join('\0')
  const [appliedFilterKey, setAppliedFilterKey] = useState(filterKey)
  const filterChanged = appliedFilterKey !== filterKey
  if (filterChanged) {
    setAppliedFilterKey(filterKey)
    setPage(0)
  }
  const queryPage = filterChanged ? 0 : page

  useEffect(() => {
    setActionTypeDraft(actionType)
    setLastErrorDraft(lastErrorContains)
  }, [actionType, lastErrorContains])

  useEffect(() => {
    if (rawSubjectCuid !== '' && rawSubjectCuid.trim() === '') {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.delete('subjectCuid')
          return next
        },
        { replace: true }
      )
    }
  }, [rawSubjectCuid, setSearchParams])

  const setFilter = (key: string, value: string): void => {
    const trimmed = value.trim()
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (trimmed === '') {
        next.delete(key)
      } else {
        next.set(key, trimmed)
      }
      return next
    })
  }

  const applyActionType = (): void => {
    if (actionTypeDraft.trim() !== actionType.trim()) {
      setFilter('actionType', actionTypeDraft)
    }
  }

  const applyLastError = (): void => {
    if (lastErrorDraft.trim() !== lastErrorContains.trim()) {
      setFilter('lastErrorContains', lastErrorDraft)
    }
  }

  const queueQuery = useGetConductorOutboxQuery({
    status: status === '' ? undefined : status,
    actionType: actionType.trim() === '' ? undefined : actionType.trim(),
    lastErrorContains: lastErrorContains.trim() === '' ? undefined : lastErrorContains.trim(),
    autonomyTier: autonomyTier === '' ? undefined : autonomyTier,
    subjectCuid: subjectCuid === '' ? undefined : subjectCuid,
    resultsPerPage: PAGE_SIZE,
    page: queryPage + 1,
  })

  const queueData = queueQuery.data
  useClampedPage(queueData?.total, queueData?.resultsPerPage, page, setPage)

  const listMode =
    queueData == null
      ? null
      : paginatedListMode({
          total: queueData.total,
          results: queueData.results,
          resultsPerPage: queueData.resultsPerPage,
        })

  return (
    <div className="space-y-4 p-4">
      <div>
        <h2 className="m-0 text-lg font-semibold text-slate-900 dark:text-slate-100">Outbox</h2>
        <p className="m-0 text-xs text-slate-500 dark:text-slate-400">
          All executor rows, including AUTO PENDING and FAILED. Approvals stays the human APPROVE queue.
        </p>
        <ConductorFieldKey screen="outbox" />
      </div>

      <Panel header="Executor rows">
        {subjectCuid !== '' ? (
          <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
            <span>
              Filtered to subject <span className="font-mono">{subjectCuid}</span>
            </span>
            <button
              type="button"
              className="text-indigo-600 hover:underline dark:text-indigo-400"
              onClick={() => setFilter('subjectCuid', '')}
            >
              Clear subject filter
            </button>
          </div>
        ) : null}
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400">
            Status
            <select
              aria-label="Filter by status"
              value={status}
              onChange={(e) => setFilter('status', e.target.value)}
              className={selectClasses}
            >
              <option value="">All statuses</option>
              {OUTBOX_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400">
            Autonomy
            <select
              aria-label="Filter by autonomy tier"
              value={autonomyTier}
              onChange={(e) => setFilter('autonomyTier', e.target.value)}
              className={selectClasses}
            >
              <option value="">All tiers</option>
              {AUTONOMY_TIERS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400">
            Action type
            <input
              type="search"
              aria-label="Filter by action type"
              value={actionTypeDraft}
              onChange={(e) => setActionTypeDraft(e.target.value)}
              onBlur={applyActionType}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  applyActionType()
                }
              }}
              maxLength={FILTER_TEXT_MAX}
              placeholder="contains…"
              className={inputClasses}
            />
          </label>
          <label className="flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400">
            Last error
            <input
              type="search"
              aria-label="Filter by last error"
              value={lastErrorDraft}
              onChange={(e) => setLastErrorDraft(e.target.value)}
              onBlur={applyLastError}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  applyLastError()
                }
              }}
              maxLength={FILTER_TEXT_MAX}
              placeholder="contains…"
              className={inputClasses}
            />
          </label>
        </div>

        {queueQuery.isError ? (
          <QueryError message="Failed to load the outbox." onRetry={() => void queueQuery.refetch()} />
        ) : queueData == null ? (
          <div className="py-6 text-center text-sm text-slate-400 dark:text-slate-500">Loading…</div>
        ) : listMode === 'empty' ? (
          <div className="py-6 text-center text-sm text-slate-400 dark:text-slate-500">
            No outbox rows match the current filters.
          </div>
        ) : (
          <>
            {listMode === 'page-pending' && (
              <div className="py-6 text-center text-sm text-slate-400 dark:text-slate-500">Loading page…</div>
            )}
            {listMode === 'content' && (
              <table className="w-full">
                <thead>
                  <tr>
                    <TH>Created</TH>
                    <TH>Action type</TH>
                    <TH>Tool</TH>
                    <TH>Status</TH>
                    <TH>Last error</TH>
                    <TH>Autonomy</TH>
                    <TH>Subject</TH>
                    <TH>Actions</TH>
                  </tr>
                </thead>
                <tbody>
                  {queueData.results.map((action) => (
                    <OutboxRow
                      key={action.cuid}
                      action={action}
                      canApprove={canApprove}
                      busy={busyCuids.has(action.cuid)}
                      outcome={outcomes[action.cuid]}
                      mutationError={mutationErrors[action.cuid]}
                      onRetry={() => void handleRetry(action.cuid)}
                      onCancel={(reason) => void handleCancel(action.cuid, reason)}
                    />
                  ))}
                </tbody>
              </table>
            )}
            {shouldShowPaginator(queueData.total, queueData.resultsPerPage) && (
              <Paginator
                className="-mx-4 mt-2 border-t border-slate-200 dark:border-slate-700"
                data={{
                  total: queueData.total,
                  resultsPerPage: queueData.resultsPerPage,
                  page,
                }}
                handlePaginatorClick={setPage}
              />
            )}
          </>
        )}
      </Panel>
    </div>
  )
}

export default Outbox
